/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import type { TelemetryHandler } from "@lumi/player-core";
import type { ReactElement, ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo } from "react";
import { useAuth } from "../auth/AuthContext";

// Simple telemetry service interface to avoid import issues
interface SimpleTelemetryService {
  track: (type: string, action: string, metadata?: Record<string, unknown>, assetId?: string) => void;
  setUser: (userId: string | null, accessToken?: string) => void;
  flush: () => Promise<void>;
}

interface TelemetryContextValue {
  telemetry: SimpleTelemetryService;
  playerTelemetryHandler: TelemetryHandler;
  trackNavigation: (path: string, metadata?: Record<string, unknown>) => void;
  trackUserAction: (action: string, metadata?: Record<string, unknown>) => void;
  trackContentInteraction: (action: string, contentId: string, metadata?: Record<string, unknown>) => void;
  trackSearch: (query: string, results: number, metadata?: Record<string, unknown>) => void;
}

const TelemetryContext = createContext<TelemetryContextValue | null>(null);

export function useTelemetry(): TelemetryContextValue {
  const context = useContext(TelemetryContext);
  if (!context) {
    throw new Error("useTelemetry must be used within a TelemetryProvider");
  }
  return context;
}

interface TelemetryProviderProps {
  readonly children: ReactNode;
  readonly apiBase?: string;
}

// TELEM-03 Phase 0.3: Device and platform detection
function getDeviceInfo(): { deviceType: string; platform: string } {
  const ua = navigator.userAgent;
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
  const isApple = /Mac|iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);

  // Determine device type (order matters: tablet check before mobile)
  const deviceType = isTablet ? "tablet" : isMobile ? "mobile" : "desktop"; // NOSONAR - intentional nested ternary for device detection

  // Determine platform
  let platform: "ios/macos" | "android" | "web";
  if (isApple) {
    platform = "ios/macos";
  } else if (isAndroid) {
    platform = "android";
  } else {
    platform = "web";
  }

  return { deviceType, platform };
}

export function TelemetryProvider({ children, apiBase }: TelemetryProviderProps): ReactElement {
  const { user, accessToken } = useAuth();
  const deviceInfo = useMemo(() => getDeviceInfo(), []);

  // Helper function to send telemetry batch to API
  const sendBatch = (
    eventsToSend: Array<{
      id: string;
      userId: string | null;
      sessionId: string;
      timestamp: string;
      type: string;
      action: string;
      assetId?: string;
      metadata?: Record<string, unknown>;
    }>,
    sessionId: string,
    actualApiBase: string,
    currentAccessToken: string | undefined,
    onComplete: () => void,
  ): void => {
    const batch = {
      events: eventsToSend,
      sessionId,
      timestamp: new Date().toISOString(),
    };

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (currentAccessToken) {
      headers.Authorization = `Bearer ${currentAccessToken}`;
    }

    fetch(`${actualApiBase}/v1/events`, {
      method: "POST",
      headers,
      body: JSON.stringify(batch),
    })
      .catch(console.error)
      .finally(onComplete);
  };

  // Helper function to auto-flush events when threshold is reached
  const autoFlushEvents = (
    events: Array<{
      id: string;
      userId: string | null;
      sessionId: string;
      timestamp: string;
      type: string;
      action: string;
      assetId?: string;
      metadata?: Record<string, unknown>;
    }>,
    sessionId: string,
    actualApiBase: string,
    currentAccessToken: string | undefined,
    setFlushing: (value: boolean) => void,
  ): void => {
    setFlushing(true);
    // Use setTimeout to avoid blocking
    setTimeout(() => {
      // Create a local copy and clear array
      const eventsToSend = [...events];
      events.length = 0;

      // Send the batch using helper function
      sendBatch(eventsToSend, sessionId, actualApiBase, currentAccessToken, () => {
        setFlushing(false);
      });
    }, 0);
  };

  // Simple telemetry implementation
  const telemetry = useMemo((): SimpleTelemetryService => {
    const defaultApiBase = import.meta.env.VITE_API_BASE_URL || "http://localhost:3333";
    const actualApiBase = apiBase || defaultApiBase;
    const events: Array<{
      id: string;
      userId: string | null;
      sessionId: string;
      timestamp: string;
      type: string;
      action: string;
      assetId?: string;
      metadata?: Record<string, unknown>;
    }> = [];

    let currentUserId: string | null = null;
    let currentAccessToken: string | undefined;
    let isFlushing = false;
    const sessionId = crypto.randomUUID();

    return {
      setUser: (userId: string | null, token?: string) => {
        currentUserId = userId;
        currentAccessToken = token;
      },

      track: (type: string, action: string, metadata?: Record<string, unknown>, assetId?: string) => {
        // TELEM-03 Phase 0.3: Enrich all events with device/platform metadata
        const enrichedMetadata = {
          ...metadata,
          deviceType: deviceInfo.deviceType,
          platform: deviceInfo.platform,
        };

        const event = {
          id: crypto.randomUUID(),
          userId: currentUserId,
          sessionId,
          timestamp: new Date().toISOString(),
          type,
          action,
          assetId,
          metadata: enrichedMetadata,
        };

        events.push(event);

        // Auto-flush when we have 10+ events (prevent race condition with flag)
        if (events.length >= 10 && !isFlushing) {
          autoFlushEvents(events, sessionId, actualApiBase, currentAccessToken, (value) => {
            isFlushing = value;
          });
        }
      },

      flush: async () => {
        if (events.length === 0) return;

        const batch = {
          events: [...events],
          sessionId,
          timestamp: new Date().toISOString(),
        };

        events.length = 0; // Clear events

        try {
          const headers: HeadersInit = {
            "Content-Type": "application/json",
          };

          if (currentAccessToken) {
            headers.Authorization = `Bearer ${currentAccessToken}`;
          }

          await fetch(`${actualApiBase}/v1/events`, {
            method: "POST",
            headers,
            body: JSON.stringify(batch),
          });
        } catch (error) {
          console.error("Failed to send telemetry batch:", error);
        }
      },
    };
  }, [apiBase]);

  // Update user when auth changes
  useEffect(() => {
    telemetry.setUser(user?.id || null, accessToken || undefined);
  }, [user, accessToken, telemetry]);

  // Create player telemetry handler
  const playerTelemetryHandler: TelemetryHandler = useMemo(
    () => ({
      trackVideoStart: (assetId: string, metadata?: Record<string, unknown>) => {
        telemetry.track("playback", "video_start", metadata, assetId);
      },
      trackVideoPause: (assetId: string, position: number) => {
        telemetry.track("playback", "video_pause", { position }, assetId);
      },
      trackVideoResume: (assetId: string, position: number) => {
        telemetry.track("playback", "video_resume", { position }, assetId);
      },
      trackVideoSeek: (assetId: string, fromPosition: number, toPosition: number) => {
        telemetry.track(
          "playback",
          "video_seek",
          {
            fromPosition,
            toPosition,
            seekDistance: Math.abs(toPosition - fromPosition),
          },
          assetId,
        );
      },
      trackVideoComplete: (assetId: string, duration: number) => {
        telemetry.track("playback", "video_complete", { duration }, assetId);
      },
      trackVideoAbandon: (assetId: string, position: number, duration: number) => {
        telemetry.track(
          "playback",
          "video_abandon",
          {
            position,
            duration,
            watchPercentage: duration > 0 ? (position / duration) * 100 : 0,
          },
          assetId,
        );
      },
      trackPlayback: (action: string, assetId: string, metadata?: Record<string, unknown>) => {
        telemetry.track("playback", action, metadata, assetId);
      },
    }),
    [telemetry],
  );

  // Helper functions for common tracking patterns
  const trackNavigation = (path: string, metadata?: Record<string, unknown>) => {
    telemetry.track("navigation", "page_view", { path, ...metadata });
  };

  const trackUserAction = (action: string, metadata?: Record<string, unknown>) => {
    telemetry.track("user", action, metadata);
  };

  const trackContentInteraction = (action: string, contentId: string, metadata?: Record<string, unknown>) => {
    telemetry.track("content", action, metadata, contentId);
  };

  const trackSearch = (query: string, results: number, metadata?: Record<string, unknown>) => {
    telemetry.track("search", "query", { query, results, ...metadata });
  };

  const value: TelemetryContextValue = useMemo(
    () => ({
      telemetry,
      playerTelemetryHandler,
      trackNavigation,
      trackUserAction,
      trackContentInteraction,
      trackSearch,
    }),
    [telemetry, playerTelemetryHandler, trackNavigation, trackUserAction, trackContentInteraction, trackSearch],
  );

  return <TelemetryContext.Provider value={value}>{children}</TelemetryContext.Provider>;
}
